/**
 * useGuideHighlight — reads `?guide=<kebab key>` once, strips it (keeping every
 * other param), then expands / scrolls / pulses the matching guide anchor.
 * Unknown, empty, repeated or camelCase keys are a no-op but still stripped (AC-047).
 *
 * Mount ONCE per page root (IntegrationsPage, AnalyticsSettingsPage,
 * StripeIntegrationPage, PaywallProductsPage, OnboardingsPageV2,
 * OnboardingEditorModalV2). Router-dependent by design — pages own the router.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { GUIDE_QUERY_PARAM, parseGuideParam, stripQueryParam } from './guideHighlight';
import { startHighlightJob, type HighlightJob } from './guideHighlightDom';

export function useGuideHighlight(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const jobRef = useRef<HighlightJob | null>(null);

  // Unmount only: cancel a running lookup / pulse.
  useEffect(() => () => {
    jobRef.current?.cancel();
    jobRef.current = null;
  }, []);

  useEffect(() => {
    const outcome = parseGuideParam(location.search);
    if (outcome.status === 'absent') return;

    // Strip from the LIVE URL, not from this hook's snapshot: another read-once
    // consumer on the same page (`?action=deploy`, Task 4) may already have
    // replaced the URL in this commit; `history.replaceState` is synchronous.
    navigate(
      {
        pathname: location.pathname,
        search: stripQueryParam(window.location.search, GUIDE_QUERY_PARAM),
        hash: location.hash,
      },
      { replace: true },
    );

    if (outcome.status !== 'ok') return;
    // One job at a time, not keyed by testId: a second `?guide=` landing while the
    // first job's bounded 5s lookup is still in flight is dropped rather than
    // restarting the job for the new key. Accepted trade-off — `?guide=` is a
    // single read-once support-highlight signal per navigation, stripped from the
    // URL on the very first render, so a second one arriving mid-job would only
    // happen from a manual re-navigation within that ~5s window.
    if (jobRef.current) return;
    jobRef.current = startHighlightJob(outcome.testId, theme.palette.primary.main, () => {
      jobRef.current = null;
    });
  }, [location.search, location.pathname, location.hash, navigate, theme.palette.primary.main]);
}
